import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense } from "react";
import { Layout } from "@/components/Layout/Layout";
import { MicrofrontendLoader } from "@/components/MicrofrontendLoader";
import { NotFound } from "@/components/NotFound";
import { Landing } from "@/pages/Landing";
import { Dashboard } from "@/pages/Dashboard";
import { Settings } from "@/pages/Settings";
import { SettingsAIApps } from "@/pages/SettingsAIApps";
import { SettingsUsers } from "@/pages/SettingsUsers";
import { SettingsAuditLogs } from "@/pages/SettingsAuditLogs";
import { Login } from "@/pages/Auth/Login";
import { useAuthStore } from "@/stores/authStore";
import { useUserApplications } from "@/hooks/useUserApplications";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { normalizeAppRoute } from "@/utils/routeUtils";

export function AppRoutes() {
  const { checkAuth } = useAuthStore();
  const { applications, loading } = useUserApplications();
  const location = useLocation();

  const isStaticRoute = [
    "/",
    "/auth/login",
    "/dashboard",
    "/settings",
    "/settings/ai-apps",
    "/settings/users",
    "/settings/audit-logs",
  ].includes(location.pathname);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/auth/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/ai-apps"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsAIApps />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/users"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsUsers />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/audit-logs"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsAuditLogs />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Dynamic microfrontend routes */}
      {applications.map((app) => (
        <Route
          key={app.id}
          path={`${app.route}/*`}
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
                      <div className="text-sm text-gray-600">Loading application...</div>
                    </div>
                  }
                >
                  <MicrofrontendLoader
                    route={normalizeAppRoute(app.route)}
                    bundleUrl={app.app_url}
                    appName={app.appName}
                  />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          }
        />
      ))}

      {loading && !isStaticRoute && (
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Layout>
                <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
                  <div className="text-sm text-gray-600">Loading application...</div>
                </div>
              </Layout>
            </ProtectedRoute>
          }
        />
      )}

      {/* 404 fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
